import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import {
  Layers,
  Monitor,
  PlusCircle,
  Printer,
  ShieldAlert,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

import { type StationWithDetails, stationsGetByLocationId } from "~/api-refs";
import { getCardColorStyle } from "~/components/stations/station-edit-types";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

type StationType = StationWithDetails;

export const Route = createFileRoute("/_authenticated/locations/$id/stations")({
  component: StationsPage,
});

// Helper to determine station status
const getStationStatus = (station: StationType) => {
  const hasScreen = station.hasScreen && station.pairedDevices.length > 0;
  const hasPrinter = !!station.printerIds?.length;
  if (hasScreen && hasPrinter) {
    return "full";
  }
  if (hasScreen || hasPrinter) {
    return "partial";
  }
  return "inactive";
};

const getStatusBadge = (status: "full" | "partial" | "inactive") => {
  switch (status) {
    case "full":
      return {
        variant: "success" as const,
        label: "Active",
        dotClass: "bg-success",
      };
    case "partial":
      return {
        variant: "warning" as const,
        label: "Partial",
        dotClass: "bg-warning",
      };
    default:
      return {
        variant: "destructive" as const,
        label: "Inactive",
        dotClass: "bg-destructive/50",
      };
  }
};

function StationsPage() {
  const { id: locationId } = Route.useParams();
  const navigate = useNavigate();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Fetch stations
  const { data: stationsData, isLoading } = useQuery({
    ...convexQuery(stationsGetByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    }),
    enabled: canViewIntegrations,
  });

  // Loading state
  if (permissionsLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  // No permission to view stations
  if (!canViewIntegrations) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to view station settings. Contact your
          manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  const handleCreateStation = () => {
    void navigate({
      to: "/locations/$id/stations/new",
      params: { id: locationId },
    });
  };

  const handleEditStation = (stationId: string) => {
    void navigate({
      to: "/locations/$id/stations/$stationId/edit",
      params: { id: locationId, stationId },
    });
  };

  return (
    <>
      <div className="border-border float-start mt-5 flex w-full flex-col gap-6 rounded-xl border p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="subtitle text-foreground">Stations</h2>
            <p className="text-muted-foreground text-sm">
              Stations are places in your kitchen that can have screens,
              printers, or both.
            </p>
          </div>
          {stationsData && stationsData.length > 0 && (
            <Button onClick={handleCreateStation} variant="default">
              <p>Add Station</p>
              <PlusCircle className="ml-2" />
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          {(() => {
            if (isLoading) {
              return (
                <div className="flex items-center justify-center p-4">
                  Loading...
                </div>
              );
            }

            if (stationsData && stationsData.length > 0) {
              return stationsData.map((station: StationType) => {
                const status = getStationStatus(station);
                const statusBadge = getStatusBadge(status);

                const cardColor = getCardColorStyle(station.color);

                return (
                  <div
                    className="flex h-52 w-56 flex-col justify-between rounded-2xl px-5 py-4"
                    key={station._id}
                    style={{
                      background: cardColor.color,
                      stroke: cardColor.stroke,
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <h3 className="subtitle-sm text-foreground">
                        {station.name}
                      </h3>
                      {station.isDefault && (
                        <Badge className="text-xs" variant="secondary">
                          Default
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1">
                      {/* Screen Info */}
                      <div className="flex flex-row items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        <p className="text-foreground text-sm">
                          {station.hasScreen
                            ? `${station.pairedDevices.length} Screen${station.pairedDevices.length !== 1 ? "s" : ""}`
                            : "No Screen"}
                        </p>
                      </div>

                      {/* Printer Info */}
                      <div className="flex flex-row items-center gap-2">
                        <Printer className="h-4 w-4" />
                        <p className="text-foreground text-sm">
                          {station.printers?.[0]?.name ?? "No Printer"}
                        </p>
                      </div>

                      {/* Routing Info */}
                      <div className="flex flex-row items-center gap-2">
                        <Layers className="h-4 w-4" />
                        <p className="text-foreground text-sm">
                          {(() => {
                            if (station.routingMode === "all") {
                              return "All Items";
                            }
                            if (station.routingMode === "categories") {
                              return `${station.categoryIds.length} Categories`;
                            }
                            return `${station.itemIds.length} Items`;
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        className="h-7 w-14"
                        onClick={() => handleEditStation(station._id)}
                        title="Edit"
                        variant="outline"
                      >
                        <p className="text-foreground">Edit</p>
                      </Button>
                      <Badge
                        className="flex w-fit items-center space-x-1 px-4 py-1"
                        variant={statusBadge.variant}
                      >
                        <div
                          className={twMerge(
                            "h-1.5 w-1.5 rounded-full",
                            statusBadge.dotClass
                          )}
                        />
                        <p>{statusBadge.label}</p>
                      </Badge>
                    </div>
                  </div>
                );
              });
            }

            return (
              <div className="flex w-full flex-col items-center gap-3 py-8">
                <Layers className="text-muted-foreground size-10" />
                <p className="text-foreground">
                  Create a Station to get started
                </p>
                <p className="text-muted-foreground max-w-md text-center text-sm">
                  A station represents a place in your kitchen like "Grill",
                  "Bar", or "Expo". Each station can have a screen (KDS), a
                  printer, or both.
                </p>
                <Button onClick={handleCreateStation} variant="default">
                  <PlusCircle className="mr-2" />
                  <p>Create Station</p>
                </Button>
              </div>
            );
          })()}
        </div>
      </div>
      <Outlet />
    </>
  );
}
